/**
 * CountDown Block - Interactivity API View Script
 *
 * Provides reactive countdown timer functionality using WordPress Interactivity API.
 *
 * @package BrandyBlocks
 */

import { store, getContext } from '@wordpress/interactivity';

/**
 * Pad a number with leading zeros.
 *
 * @param {number} num - Number to pad.
 * @return {string} Padded string (2 digits minimum).
 */
const padNumber = ( num ) => String( Math.max( 0, num ) ).padStart( 2, '0' );

/**
 * CountDown block store.
 *
 * Uses WordPress Interactivity API to provide reactive state management
 * for the countdown timer. State getters automatically recalculate
 * when context.remaining changes.
 */
store( 'brandy/countdown', {
	/**
	 * Reactive state getters.
	 * These automatically update the UI when context.remaining changes.
	 */
	state: {
		/**
		 * Get formatted days remaining.
		 *
		 * @return {string} Days as 2-digit string.
		 */
		get days() {
			const ctx = getContext();
			return padNumber( Math.floor( ctx.remaining / 86400 ) );
		},

		/**
		 * Get formatted hours remaining (0-23).
		 *
		 * @return {string} Hours as 2-digit string.
		 */
		get hours() {
			const ctx = getContext();
			return padNumber( Math.floor( ( ctx.remaining % 86400 ) / 3600 ) );
		},

		/**
		 * Get formatted minutes remaining (0-59).
		 *
		 * @return {string} Minutes as 2-digit string.
		 */
		get minutes() {
			const ctx = getContext();
			return padNumber( Math.floor( ( ctx.remaining % 3600 ) / 60 ) );
		},

		/**
		 * Get formatted seconds remaining (0-59).
		 *
		 * @return {string} Seconds as 2-digit string.
		 */
		get seconds() {
			const ctx = getContext();
			return padNumber( ctx.remaining % 60 );
		},
	},

	/**
	 * Callbacks run on specific lifecycle events.
	 */
	callbacks: {
		/**
		 * Initialize the countdown timer.
		 * Called via data-wp-init directive when block is rendered.
		 */
		startCountdown() {
			const ctx = getContext();

			// Don't start if no target date or already expired.
			if ( ctx.remaining <= 0 ) {
				return;
			}

			// Store interval reference for cleanup.
			const intervalId = setInterval( () => {
				if ( ctx.remaining > 0 ) {
					ctx.remaining--;
				} else {
					// Stop the interval when countdown reaches zero.
					clearInterval( intervalId );
				}
			}, 1000 );

			// Return cleanup function (called when block is removed from DOM).
			return () => {
				clearInterval( intervalId );
			};
		},
	},
} );
